import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { Message, User, Workspace, Project, Task, Document } from '@/types'
import {
  messageService,
  userService,
  workspaceService,
  projectService,
  taskService,
  documentService,
} from '@/services'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { messagesListConfig } from '@/lib/config/node-list-configs'
import { NodeListPage } from '@/components/nodes/NodeListPage'
import type { MessagesFiltersState } from '@/components/messages'
import type { MessageGroupByOption } from '@/lib/config/node-grouping-configs'

export default function MessagesPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [allMessages, setAllMessages] = useState<Message[]>([])
  const [availableUsers, setAvailableUsers] = useState<User[]>([])
  const [availableWorkspaces, setAvailableWorkspaces] = useState<Workspace[]>([])
  const [availableProjects, setAvailableProjects] = useState<Project[]>([])
  const [availableTasks, setAvailableTasks] = useState<Task[]>([])
  const [availableDocuments, setAvailableDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)

  // NodeListPage state
  const [searchQuery, setSearchQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [groupBy, setGroupBy] = useState<MessageGroupByOption>('none')
  const [filters, setFilters] = useState<MessagesFiltersState>(messagesListConfig.defaultFilters)

  // Handler for partial filter updates
  const handleFiltersChange = (partialFilters: Partial<MessagesFiltersState>) => {
    setFilters(
      (prev: MessagesFiltersState) => ({ ...prev, ...partialFilters }) as MessagesFiltersState
    )
  }

  // Get active tab from URL or default to 'unread' (single source of truth)
  const activeTab = searchParams.get('tab') || 'unread'

  const loadMessages = useCallback(async () => {
    if (!user) return

    try {
      setLoading(true)
      // Load messages
      const userMessages = await messageService.getMessagesForUser(user.id)
      setAllMessages(userMessages)

      // Load users that might be involved in messages
      const users = await userService.getAll()
      setAvailableUsers(users)

      // Load workspaces that might be involved in messages
      const workspaces = await workspaceService.getAll()
      setAvailableWorkspaces(workspaces)

      // Load projects that might be involved in messages
      const projects = await projectService.getAll()
      setAvailableProjects(projects)

      // Load tasks that might be involved in messages
      const tasks = await taskService.getAll()
      setAvailableTasks(tasks)

      // Load documents that might be involved in messages
      const documents = await documentService.getAll()
      setAvailableDocuments(documents)
    } catch (error) {
      console.error('Failed to load data:', error)
      toast.error('Failed to load data')
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    loadMessages()
  }, [loadMessages])

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value })
  }

  const handleMessageSelect = (message: Message) => {
    // Preserve the current tab when navigating to a message
    navigate(`/messages/${message.id}?tab=${activeTab}`)
  }

  return (
    <NodeListPage
      nodes={allMessages}
      loading={loading}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      showFilters={showFilters}
      onFiltersToggle={() => setShowFilters(!showFilters)}
      filters={filters}
      onFiltersChange={handleFiltersChange}
      groupBy={groupBy}
      onGroupByChange={setGroupBy}
      config={
        {
          ...messagesListConfig,
          filterNodes: (
            nodes: Message[],
            tab: string,
            filterState: MessagesFiltersState,
            query: string
          ) =>
            messagesListConfig.filterNodes(
              nodes,
              tab,
              filterState,
              query,
              user ? { id: user.id, role: user.role } : undefined
            ),
          filterComponentProps: {
            ...messagesListConfig.filterComponentProps,
            availableWorkspaces: availableWorkspaces,
            availableUsers: availableUsers,
            availableProjects: availableProjects,
            availableTasks: availableTasks,
            availableDocuments: availableDocuments,
            currentUserId: user?.id || '',
          },
          groupingComponentProps: {
            onMessageSelect: handleMessageSelect,
            selectedMessageId: undefined,
          },
        } as typeof messagesListConfig
      }
    />
  )
}
